import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { de } from "./de";
import { en } from "./en";
import { ar } from "./ar";

const DICTS = { de, en, ar };
const SUPPORTED = new Set(["de", "en", "ar"]);
const RTL = new Set(["ar"]);
const STORAGE_KEY = "hm_lang";

export const LANGS = [
  { code: "de", label: "Deutsch", short: "DE" },
  { code: "en", label: "English", short: "EN" },
  { code: "ar", label: "العربية", short: "AR" },
];

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return SUPPORTED.has(stored) ? stored : "de";
  });

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = RTL.has(lang) ? "rtl" : "ltr";
  }, [lang]);

  const setLang = useCallback((code) => {
    if (!DICTS[code]) return;
    localStorage.setItem(STORAGE_KEY, code);
    setLangState(code);
  }, []);

  const t = useCallback((path) => {
    const parts = path.split(".");
    for (const source of [DICTS[lang], DICTS.de]) {
      let cur = source;
      let ok = true;
      for (const p of parts) {
        if (cur && typeof cur === "object" && p in cur) cur = cur[p];
        else { ok = false; break; }
      }
      if (ok) return cur;
    }
    return path;
  }, [lang]);

  return (
    <I18nContext.Provider value={{ lang, setLang, t, isRTL: RTL.has(lang) }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    // Fallback: use German by default when consumed outside the provider
    return {
      lang: "de", setLang: () => {}, isRTL: false,
      t: (path) => {
        const parts = path.split(".");
        let cur = de;
        for (const p of parts) {
          if (cur && typeof cur === "object" && p in cur) cur = cur[p];
          else return path;
        }
        return cur;
      },
    };
  }
  return ctx;
}
